// src/pages/Rules.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Trophy, 
  Shield, 
  Star, 
  Zap, 
  Crown, 
  Medal, 
  Gem, 
  Target, 
  Users, 
  Gamepad2,
  ChevronRight,
  Award,
  Sparkles,
  TrendingUp,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  Info,
  BookOpen,
  BarChart3,
  Calendar,
  Zap as Lightning,
  ArrowUp,
  ArrowDown,
  Minus,
  Gift,
  Diamond
} from 'lucide-react';

const Rules = () => {
  const navigate = useNavigate();

  // Tier data
  const tiers = [
    {
      name: 'S',
      displayName: 'LEGEND (S)',
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-500/10',
      borderColor: 'border-yellow-500/30',
      gradient: 'from-yellow-400 to-orange-500',
      icon: <Crown className="w-8 h-8" />,
      requirements: 'Top 3 in T1',
      stayCondition: 'Top 4-10 remain in T1',
      demotion: 'Bottom teams demoted to T2',
      promotion: 'Top 3 promoted to S',
      perks: 'Direct Semi-Final entry • Exclusive rewards'
    },
    {
      name: 'T1',
      displayName: 'ELITE (T1)',
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/30',
      gradient: 'from-purple-400 to-pink-500',
      icon: <Gem className="w-8 h-8" />,
      requirements: 'Top 5 in T2',
      stayCondition: 'Top 6-10 stay in T2',
      demotion: 'Below Top 10 demoted to T2Q',
      promotion: 'Top 5 promoted to T1',
      perks: 'Direct Quarter-Final entry • Priority support'
    },
    {
      name: 'T2',
      displayName: 'PRO (T2)',
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/30',
      gradient: 'from-blue-400 to-cyan-500',
      icon: <Medal className="w-8 h-8" />,
      requirements: 'Top 5 in T2Q',
      stayCondition: 'Below Top 5 demoted to T3',
      demotion: 'Bottom teams demoted to T3',
      promotion: 'Top 5 promoted to T1',
      perks: 'Skip Qualifier matches • Entry fee discounts'
    },
    {
      name: 'T2Q',
      displayName: 'PRO (T2Q)',
      color: 'text-cyan-400',
      bgColor: 'bg-cyan-500/10',
      borderColor: 'border-cyan-500/30',
      gradient: 'from-cyan-400 to-teal-500',
      icon: <Star className="w-8 h-8" />,
      requirements: 'Top 1 in any T3 match or Top 3 in T3 tournament',
      stayCondition: 'NONE',
      demotion: 'Bottom teams demoted to T3',
      promotion: 'Top 5 promoted to T2',
      perks: 'Entry fee discounts • Access to competitive tournaments'
    },
    {
      name: 'T3',
      displayName: 'RISING (T3)',
      color: 'text-green-400',
      bgColor: 'bg-green-500/10',
      borderColor: 'border-green-500/30',
      gradient: 'from-green-400 to-emerald-500',
      icon: <Zap className="w-8 h-8" />,
      requirements: 'New players start here',
      stayCondition: 'All new players start here',
      demotion: 'NONE',
      promotion: 'Top teams promoted to T2Q',
      perks: 'Learn and grow • Beginner friendly tournaments'
    }
  ];

  // Match schedule
  const matchSchedule = [
    {
      tier: 'T3',
      icon: <Zap className="w-5 h-5 text-green-400" />,
      frequency: '10 sets everyday',
      matchesPerGroup: '2 matches per group',
      totalMatches: '20 matches daily',
      timing: 'Throughout the day'
    },
    {
      tier: 'T2Q',
      icon: <Star className="w-5 h-5 text-cyan-400" />,
      frequency: 'Everyday at night',
      matchesPerGroup: '3 matches per group',
      totalMatches: '3 matches daily',
      timing: 'Night'
    },
    {
      tier: 'T2',
      icon: <Medal className="w-5 h-5 text-blue-400" />,
      frequency: 'Every day for 3 days',
      matchesPerGroup: '3 matches daily',
      totalMatches: '9 matches total',
      timing: 'Night'
    },
    {
      tier: 'T1',
      icon: <Gem className="w-5 h-5 text-purple-400" />,
      frequency: 'Every day for 6 days',
      matchesPerGroup: '3 matches daily',
      totalMatches: '18 matches total',
      timing: 'Night'
    },
    {
      tier: 'S',
      icon: <Crown className="w-5 h-5 text-yellow-400" />,
      frequency: 'Every day for 6 days',
      matchesPerGroup: '3 matches daily',
      totalMatches: '18 matches total',
      timing: 'Night'
    }
  ];

  // Points table for team standings
  const standingPoints = [
    { position: '#1', points: 1000, color: 'from-yellow-500 to-orange-500' },
    { position: '#2', points: 750, color: 'from-gray-400 to-gray-500' },
    { position: '#3', points: 600, color: 'from-orange-500 to-amber-500' },
    { position: '#4', points: 500, color: 'from-blue-400 to-blue-500' },
    { position: '#5', points: 450, color: 'from-purple-400 to-purple-500' },
    { position: '#6 to #10', points: 350, color: 'from-cyan-400 to-cyan-500' },
    { position: '#11 to #13', points: 300, color: 'from-teal-400 to-teal-500' },
    { position: '#14', points: 200, color: 'from-green-400 to-green-500' },
    { position: '#15', points: 150, color: 'from-yellow-400 to-yellow-500' },
    { position: '#16', points: 100, color: 'from-red-400 to-red-500' },
  ];

  // Match points
  const matchPoints = [
    { position: '#1', points: 10, kills: 'Per Kill - 1', icon: '🥇' },
    { position: '#2', points: 6, kills: 'Per Kill - 1', icon: '🥈' },
    { position: '#3', points: 5, kills: 'Per Kill - 1', icon: '🥉' },
    { position: '#4', points: 4, kills: 'Per Kill - 1', icon: '4️⃣' },
    { position: '#5', points: 3, kills: 'Per Kill - 1', icon: '5️⃣' },
    { position: '#6', points: 2, kills: 'Per Kill - 1', icon: '6️⃣' },
    { position: '#7 to #8', points: 1, kills: 'Per Kill - 1', icon: '7️⃣' },
    { position: '#9 to #16', points: 0, kills: 'Per Kill - 1', icon: '🔢' },
  ];

  // S Tier retention
  const sTierRetention = [
    { rank: 'Top 1-13', status: '✅ Stay in S', color: 'text-green-400', icon: <CheckCircle className="w-4 h-4" /> },
    { rank: 'Bottom 3', status: '⬇️ Demoted to T1', color: 'text-red-400', icon: <ArrowDown className="w-4 h-4" /> },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#0f0f1a] to-[#0a0a0f]">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-cyan-500/5 rounded-full blur-3xl animate-pulse-slow" />
      </div>

      {/* Header */}
      <div className="fixed top-0 w-full bg-gradient-to-r from-[#0a0a0f]/95 via-[#0f0f1a]/95 to-[#0a0a0f]/95 backdrop-blur-xl border-b border-purple-500/20 z-40">
        <div className="px-4 md:px-8 py-3">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center gap-3">
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate(-1)} 
                className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center hover:from-purple-500/30 hover:to-blue-500/30 transition-all duration-300"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </motion.button>
              <h1 className="text-white font-rajdhani font-bold text-xl md:text-2xl bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                Rules & Information
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <div className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 px-3 py-1 rounded-full border border-purple-500/20">
                <span className="text-purple-400 font-rajdhani font-bold text-xs flex items-center gap-1">
                  <BookOpen className="w-3 h-3" />
                  ARENA RULES
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="pt-20 pb-12 px-4 md:px-8 max-w-7xl mx-auto">
        
        {/* ============================================ */}
        {/* SECTION A: Tier Progression System */}
        {/* ============================================ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-5 md:p-6 border border-purple-500/30 mb-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 flex items-center justify-center">
              <Trophy className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-white font-rajdhani font-bold text-xl md:text-2xl">
              Tier Progression System
            </h2>
          </div>

          {/* Tier Cards */}
          <div className="space-y-4">
            {tiers.map((tier, idx) => (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + idx * 0.08 }}
                className={`relative rounded-xl p-4 border ${tier.bgColor} ${tier.borderColor} hover:border-opacity-100 transition-all`}
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex items-center gap-3 md:w-1/4">
                    <div className={tier.color}>
                      {tier.icon}
                    </div>
                    <div>
                      <div className={`font-rajdhani font-bold text-lg ${tier.color}`}>
                        {tier.displayName}
                      </div>
                      <p className="text-gray-500 text-xs">{tier.requirements}</p>
                    </div>
                  </div>
                  
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                    <div className="bg-white/5 rounded-lg p-2">
                      <p className="text-gray-400">Promotion</p>
                      <p className="text-green-400 font-rajdhani font-bold">{tier.promotion}</p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-2">
                      <p className="text-gray-400">Stay</p>
                      <p className="text-yellow-400 font-rajdhani font-bold">{tier.stayCondition}</p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-2">
                      <p className="text-gray-400">Demotion</p>
                      <p className="text-red-400 font-rajdhani font-bold">{tier.demotion}</p>
                    </div>
                  </div>
                  
                  <div className="md:w-1/5 text-right">
                    <p className="text-gray-400 text-xs">Perks</p>
                    <p className="text-gray-300 text-xs">{tier.perks}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* S Tier Special */}
          <div className="mt-4 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-xl p-4 border border-yellow-500/30">
            <div className="flex items-center gap-3 mb-3">
              <Crown className="w-5 h-5 text-yellow-500" />
              <h3 className="text-white font-rajdhani font-bold">S Tier Retention</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {sTierRetention.map((item, idx) => (
                <div key={idx} className={`flex items-center justify-between bg-white/5 rounded-lg p-3 ${item.color}`}>
                  <span className="font-rajdhani font-bold">{item.rank}</span>
                  <span className="flex items-center gap-1">{item.icon} {item.status}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* ============================================ */}
        {/* SECTION B: Match Timing & Details */}
        {/* ============================================ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-5 md:p-6 border border-purple-500/30 mb-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-white font-rajdhani font-bold text-xl md:text-2xl">
              Match Timing & Details
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {matchSchedule.map((schedule, idx) => (
              <motion.div
                key={schedule.tier}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 + idx * 0.08 }}
                className="bg-white/5 rounded-xl p-4 border border-white/10 hover:border-purple-500/30 transition-all"
              >
                <div className="flex items-center gap-2 mb-3">
                  {schedule.icon}
                  <span className={`font-rajdhani font-bold text-lg text-${schedule.tier === 'S' ? 'yellow' : schedule.tier === 'T1' ? 'purple' : schedule.tier === 'T2' ? 'blue' : schedule.tier === 'T2Q' ? 'cyan' : 'green'}-400`}>
                    Tier {schedule.tier}
                  </span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Frequency</span>
                    <span className="text-white font-rajdhani">{schedule.frequency}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Per Group</span>
                    <span className="text-white font-rajdhani">{schedule.matchesPerGroup}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Total</span>
                    <span className="text-white font-rajdhani font-bold">{schedule.totalMatches}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Timing</span>
                    <span className="text-yellow-400 font-rajdhani">{schedule.timing}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* ============================================ */}
        {/* SECTION C: Team Standing Points */}
        {/* ============================================ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-5 md:p-6 border border-purple-500/30 mb-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-white font-rajdhani font-bold text-xl md:text-2xl">
              Team Standing Points
            </h2>
            <span className="ml-auto text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded-full">
              Open For All Tournaments
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {standingPoints.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 + idx * 0.05 }}
                className={`bg-gradient-to-br ${item.color} rounded-xl p-3 text-center text-white`}
              >
                <p className="text-xs font-rajdhani opacity-80">Position</p>
                <p className="font-rajdhani font-bold text-sm">{item.position}</p>
                <p className="font-rajdhani font-bold text-xl">{item.points}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-4 text-center text-gray-400 text-xs">
            <p>Points are awarded based on final team standings in open tournaments</p>
          </div>
        </motion.div>

        {/* ============================================ */}
        {/* SECTION D: Rewards by Level */}
        {/* ============================================ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-5 md:p-6 border border-purple-500/30 mb-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
              <Gift className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-white font-rajdhani font-bold text-xl md:text-2xl">
              Rewards by Level
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-xl p-4 border border-yellow-500/30 text-center">
              <Crown className="w-10 h-10 text-yellow-500 mx-auto mb-2" />
              <h3 className="text-yellow-400 font-rajdhani font-bold text-lg">S Tier</h3>
              <p className="text-gray-300 text-sm">Direct Semi-Final Entry</p>
              <p className="text-gray-400 text-xs mt-2">Skip preliminary rounds</p>
            </div>
            <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl p-4 border border-purple-500/30 text-center">
              <Gem className="w-10 h-10 text-purple-400 mx-auto mb-2" />
              <h3 className="text-purple-400 font-rajdhani font-bold text-lg">T1 Tier</h3>
              <p className="text-gray-300 text-sm">Direct Quarter-Final Entry</p>
              <p className="text-gray-400 text-xs mt-2">Skip initial rounds</p>
            </div>
            <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl p-4 border border-blue-500/30 text-center">
              <Medal className="w-10 h-10 text-blue-400 mx-auto mb-2" />
              <h3 className="text-blue-400 font-rajdhani font-bold text-lg">T2 Tier</h3>
              <p className="text-gray-300 text-sm">Skip Qualifier Matches</p>
              <p className="text-gray-400 text-xs mt-2">Direct tournament entry</p>
            </div>
          </div>
        </motion.div>

        {/* ============================================ */}
        {/* SECTION E: Match Points System */}
        {/* ============================================ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-5 md:p-6 border border-purple-500/30 mb-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-white font-rajdhani font-bold text-xl md:text-2xl">
              Match Points System
            </h2>
            <span className="ml-auto text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded-full">
              Per Match
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {matchPoints.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 + idx * 0.05 }}
                className="bg-white/5 rounded-xl p-3 text-center border border-white/10 hover:border-purple-500/30 transition-all"
              >
                <p className="text-2xl">{item.icon}</p>
                <p className="text-white font-rajdhani font-bold text-sm">{item.position}</p>
                <p className="text-yellow-400 font-rajdhani font-bold text-lg">{item.points} pts</p>
                <p className="text-gray-500 text-xs">{item.kills}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-4 bg-green-500/10 rounded-lg p-3 border border-green-500/30 text-center">
            <p className="text-green-400 font-rajdhani font-bold text-sm">
              💀 Per Kill: <span className="text-white">+1 Point</span>
            </p>
          </div>
        </motion.div>

        {/* ============================================ */}
        {/* SECTION F: Additional Rules */}
        {/* ============================================ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl p-5 md:p-6 border border-purple-500/30"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-red-500 to-pink-500 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-white font-rajdhani font-bold text-xl md:text-2xl">
              Additional Rules & Guidelines
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Rule 1 */}
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <h3 className="text-white font-rajdhani font-bold">BGMI Official Rules</h3>
              </div>
              <p className="text-gray-400 text-sm">
                All official BGMI rules and regulations are strictly applied in every tournament.
              </p>
            </div>

            {/* Rule 2 */}
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-4 h-4 text-yellow-400" />
                <h3 className="text-white font-rajdhani font-bold">Final Authority</h3>
              </div>
              <p className="text-gray-400 text-sm">
                The final decision for any dispute or issue rests with TheArenaX Official Management.
              </p>
            </div>

            {/* Rule 3 */}
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-4 h-4 text-yellow-400" />
                <h3 className="text-white font-rajdhani font-bold">Fair Play</h3>
              </div>
              <p className="text-gray-400 text-sm">
                Any form of cheating, hacking, or unfair advantage will result in immediate ban and disqualification.
              </p>
            </div>

            {/* Rule 4 */}
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-blue-400" />
                <h3 className="text-white font-rajdhani font-bold">Respect & Sportsmanship</h3>
              </div>
              <p className="text-gray-400 text-sm">
                All players must maintain respect towards opponents, admins, and fellow participants at all times.
              </p>
            </div>

            {/* Rule 5 */}
            <div className="bg-white/5 rounded-xl p-4 border border-white/10 md:col-span-2">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-blue-400" />
                <h3 className="text-white font-rajdhani font-bold">Timeline & Reporting</h3>
              </div>
              <p className="text-gray-400 text-sm">
                All issues must be reported within 10 minutes of match completion. Late reports will not be entertained. 
                Prize distribution is processed within 24 hours of tournament completion.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Footer Note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="mt-6 text-center text-gray-500 text-xs"
        >
          <p>Last Updated: January 2026 • TheArenaX Official Rules</p>
        </motion.div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@300;400;500;600;700&display=swap');
        
        .font-rajdhani {
          font-family: 'Rajdhani', sans-serif;
        }
        
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.3; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 0.6; transform: translate(-50%, -50%) scale(1.1); }
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default Rules;